import * as z from 'zod'

import type {
  AffiliateEuIbanRailSchema,
  AffiliateIntlSwiftRailSchema,
  AffiliatePaymentMethodRequestSchema,
  AffiliatePaymentMethodRequestSchemaBankDetailsAnyOf,
  AffiliatePayoutAddressSchema,
  AffiliateUsAchRailSchema,
} from '@/api/gen'

/**
 * Single owner of the affiliate payout-method vocabulary: the method + wire-rail
 * enums, human labels, the field definitions that drive the rail-conditional
 * write forms, the client-side validation that mirrors the server's
 * discriminated-union rules, and the detail-summary formatting used by the CSV
 * export and the read views.
 *
 * Before this module the same knowledge was copied across four call sites
 * (`settings-page` METHOD_FIELDS, `payout-info-card` METHOD_FIELDS,
 * `admin-payouts-page` formatPayoutDetails, `settlements/labels` METHOD_LABELS)
 * and drifted (the list page labelled CRYPTO "Crypto" while it settles through
 * Confirmo). Every payout-method surface now reads from here.
 *
 * The server is the ultimate authority — every write is re-validated by the
 * Ninja discriminated union and 422s with field errors. This client validation
 * exists to catch mistakes before the round-trip, not to replace the server.
 */

// ─── Methods ────────────────────────────────────────────────────────────────

export const PAYOUT_METHODS = ['PAYPAL', 'WISE', 'CRYPTO', 'WIRE'] as const
export type PayoutMethod = (typeof PAYOUT_METHODS)[number]

export const isPayoutMethod = (value: unknown): value is PayoutMethod =>
  typeof value === 'string' && (PAYOUT_METHODS as readonly string[]).includes(value)

// WIRE and WISE both collect bank details (discriminated by rail); PAYPAL and
// CRYPTO collect their own flat shapes.
const BANK_METHODS: readonly PayoutMethod[] = ['WIRE', 'WISE']
export const usesBankRail = (method: PayoutMethod): boolean => BANK_METHODS.includes(method)

/**
 * Human labels for the four payout methods. Enum values, not free text, so they
 * live as a static map (mirrors `settlements/labels.ts` PROVIDER_LABELS). CRYPTO
 * reads "Crypto (Confirmo)" everywhere because Confirmo is the crypto settlement
 * provider — the label the payouts filter and settlement channel overview want.
 */
export const METHOD_LABELS: Record<PayoutMethod, string> = {
  PAYPAL: 'PayPal',
  WISE: 'Wise',
  CRYPTO: 'Crypto (Confirmo)',
  WIRE: 'Wire',
}

/** Guarded label lookup — returns the raw value for legacy/unknown methods. */
export const methodLabel = (method: string): string =>
  isPayoutMethod(method) ? METHOD_LABELS[method] : method

// ─── Wire rails ─────────────────────────────────────────────────────────────

export const WIRE_RAILS = ['EU_IBAN', 'US_ACH', 'INTL_SWIFT'] as const
export type WireRail = (typeof WIRE_RAILS)[number]

export const isWireRail = (value: unknown): value is WireRail =>
  typeof value === 'string' && (WIRE_RAILS as readonly string[]).includes(value)

export const RAIL_LABEL_IDS: Record<WireRail, string> = {
  EU_IBAN: 'payout.rail.euIban',
  US_ACH: 'payout.rail.usAch',
  INTL_SWIFT: 'payout.rail.intlSwift',
}

// ─── Field definitions (drive the form) ─────────────────────────────────────

type SelectOption = { value: string; labelId: string }

export type PayoutFieldSpec = {
  /** Flat key in the form's value record and (for detail keys) the wire body. */
  name: string
  labelId: string
  placeholder?: string
  mono?: boolean
  kind?: 'text' | 'select'
  options?: readonly SelectOption[]
}

export type PayoutFormShape = {
  fields: PayoutFieldSpec[]
  /** Whether the postal-address block renders after the flat fields. */
  includeAddress: boolean
}

const CRYPTO_NETWORK_OPTIONS: readonly SelectOption[] = [
  { value: 'BTC', labelId: 'payout.network.btc' },
  { value: 'USDC (erc20)', labelId: 'payout.network.usdcErc20' },
]

const US_ACCOUNT_TYPE_OPTIONS: readonly SelectOption[] = [
  { value: 'CHECKING', labelId: 'payout.accountType.checking' },
  { value: 'SAVINGS', labelId: 'payout.accountType.savings' },
]

const WIRE_RECEIVER_TYPE_OPTIONS: readonly SelectOption[] = [
  { value: 'PERSON', labelId: 'payout.receiverType.person' },
  { value: 'INSTITUTION', labelId: 'payout.receiverType.institution' },
]

const RAIL_FIELDS: Record<WireRail, PayoutFieldSpec[]> = {
  EU_IBAN: [
    { name: 'holder', labelId: 'payout.field.holder' },
    {
      name: 'iban',
      labelId: 'payout.field.iban',
      placeholder: 'GB29 NWBK 6016 1331 9268 19',
      mono: true,
    },
    { name: 'bic', labelId: 'payout.field.bic', placeholder: 'NWBKGB2L', mono: true },
  ],
  US_ACH: [
    { name: 'holder', labelId: 'payout.field.holder' },
    {
      name: 'routing_number',
      labelId: 'payout.field.routingNumber',
      placeholder: '021000021',
      mono: true,
    },
    { name: 'account_number', labelId: 'payout.field.accountNumber', mono: true },
    {
      name: 'account_type',
      labelId: 'payout.field.accountType',
      kind: 'select',
      options: US_ACCOUNT_TYPE_OPTIONS,
    },
    {
      name: 'receiver_type',
      labelId: 'payout.field.receiverType',
      kind: 'select',
      options: WIRE_RECEIVER_TYPE_OPTIONS,
    },
  ],
  INTL_SWIFT: [
    { name: 'holder', labelId: 'payout.field.holder' },
    { name: 'account_number', labelId: 'payout.field.accountNumber', mono: true },
    { name: 'swift', labelId: 'payout.field.swift', placeholder: 'DEUTDEFF', mono: true },
    { name: 'bank_name', labelId: 'payout.field.bankName' },
    { name: 'tax_id', labelId: 'payout.field.taxId' },
  ],
}

const PAYPAL_FIELDS: PayoutFieldSpec[] = [
  { name: 'email', labelId: 'payout.field.email', placeholder: 'you@example.com' },
]

const CRYPTO_FIELDS: PayoutFieldSpec[] = [
  {
    name: 'network',
    labelId: 'payout.field.network',
    kind: 'select',
    options: CRYPTO_NETWORK_OPTIONS,
  },
  {
    name: 'address',
    labelId: 'payout.field.walletAddress',
    placeholder: '0x… / bc1… / T…',
    mono: true,
  },
]

export const ADDRESS_FIELDS: PayoutFieldSpec[] = [
  { name: 'line', labelId: 'payout.address.line' },
  { name: 'city', labelId: 'payout.address.city' },
  { name: 'state', labelId: 'payout.address.state' },
  { name: 'post_code', labelId: 'payout.address.postCode' },
  { name: 'country_code', labelId: 'payout.address.countryCode', placeholder: 'US' },
]

/**
 * The fields to render for a given method (+ rail for the bank methods).
 * `null` rail on a bank method means "no rail chosen yet" → only the rail
 * select renders (handled by the form component), no field rows.
 */
export const getPayoutFormShape = (
  method: PayoutMethod,
  rail: WireRail | null,
): PayoutFormShape => {
  if (method === 'PAYPAL') {
    return { fields: PAYPAL_FIELDS, includeAddress: false }
  }
  if (method === 'CRYPTO') {
    return { fields: CRYPTO_FIELDS, includeAddress: false }
  }
  if (rail === null) {
    return { fields: [], includeAddress: false }
  }

  return { fields: RAIL_FIELDS[rail], includeAddress: true }
}

// ─── Validation (mirrors the server) ────────────────────────────────────────
// Transcribed from gto-wizard
// app/app/account/affiliates/services/payout_details_validation.py so the client
// rejects the same inputs the server would 422 on.

const IBAN_LENGTH_BY_COUNTRY: Record<string, number> = {
  AD: 24,
  AE: 23,
  AL: 28,
  AT: 20,
  AZ: 28,
  BA: 20,
  BE: 16,
  BG: 22,
  BH: 22,
  BR: 29,
  BY: 28,
  CH: 21,
  CR: 22,
  CY: 28,
  CZ: 24,
  DE: 22,
  DK: 18,
  DO: 28,
  EE: 20,
  EG: 29,
  ES: 24,
  FI: 18,
  FO: 18,
  FR: 27,
  GB: 22,
  GE: 22,
  GI: 23,
  GL: 18,
  GR: 27,
  GT: 28,
  HR: 21,
  HU: 28,
  IE: 22,
  IL: 23,
  IS: 26,
  IT: 27,
  JO: 30,
  KW: 30,
  KZ: 20,
  LB: 28,
  LC: 32,
  LI: 21,
  LT: 20,
  LU: 20,
  LV: 21,
  MC: 27,
  MD: 24,
  ME: 22,
  MK: 19,
  MR: 27,
  MT: 31,
  MU: 30,
  NL: 18,
  NO: 15,
  PK: 24,
  PL: 28,
  PS: 29,
  PT: 25,
  QA: 29,
  RO: 24,
  RS: 22,
  SA: 24,
  SC: 31,
  SE: 24,
  SI: 19,
  SK: 24,
  SM: 27,
  ST: 25,
  SV: 28,
  TL: 23,
  TN: 24,
  TR: 26,
  UA: 29,
  VA: 22,
  VG: 24,
  XK: 20,
}

const NON_IBAN_ISO_COUNTRIES = [
  'US',
  'CA',
  'JP',
  'AU',
  'NZ',
  'SG',
  'HK',
  'ZA',
  'MX',
  'AR',
  'CL',
  'TH',
  'PH',
  'VN',
  'ID',
  'MY',
  'KR',
  'TW',
  'CN',
  'IN',
] as const

const ISO_COUNTRY_CODES: ReadonlySet<string> = new Set<string>([
  ...Object.keys(IBAN_LENGTH_BY_COUNTRY),
  ...NON_IBAN_ISO_COUNTRIES,
])

export const isIsoCountryCode = (raw: string): boolean => {
  const candidate = raw.trim().toUpperCase()

  return candidate.length === 2 && /^[A-Z]{2}$/.test(candidate) && ISO_COUNTRY_CODES.has(candidate)
}

export const normalizeIban = (raw: string): string => raw.replace(/\s+/g, '').toUpperCase()

// Piecewise mod-97 (ISO 13616): the full IBAN as an integer overflows a JS
// number, so fold the remainder digit-group by digit-group. Letters map to
// their two-digit value (A=10 … Z=35) with the check digits rotated to the end.
const ibanChecksumOk = (iban: string): boolean => {
  const rearranged = iban.slice(4) + iban.slice(0, 4)
  let remainder = 0
  for (const char of rearranged) {
    const mapped = char >= 'A' && char <= 'Z' ? String(char.charCodeAt(0) - 55) : char
    for (const digit of mapped) {
      remainder = (remainder * 10 + Number(digit)) % 97
    }
  }

  return remainder === 1
}

export const isValidIban = (raw: string): boolean => {
  const candidate = normalizeIban(raw)
  if (candidate.length < 4 || !/^[A-Z]{2}\d{2}/.test(candidate)) {
    return false
  }
  const expectedLength = IBAN_LENGTH_BY_COUNTRY[candidate.slice(0, 2)]
  if (expectedLength === undefined || candidate.length !== expectedLength) {
    return false
  }
  if (!/^[A-Z0-9]+$/.test(candidate.slice(4))) {
    return false
  }

  return ibanChecksumOk(candidate)
}

export const normalizeBic = (raw: string): string => raw.replace(/\s+/g, '').toUpperCase()

export const isValidBic = (raw: string): boolean => {
  const candidate = normalizeBic(raw)
  if (!/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(candidate)) {
    return false
  }

  return ISO_COUNTRY_CODES.has(candidate.slice(4, 6))
}

export const isValidAbaRouting = (raw: string): boolean => {
  const candidate = raw.trim()
  if (!/^\d{9}$/.test(candidate)) {
    return false
  }
  // ABA weights cycle 3-7-1 across the nine digits. Walk by index off the ASCII
  // code point (candidate is nine digits) so the sum stays a plain number.
  let checksum = 0
  for (let i = 0; i < 9; i += 1) {
    const weight = i % 3 === 0 ? 3 : i % 3 === 1 ? 7 : 1
    checksum += (candidate.charCodeAt(i) - 48) * weight
  }

  return checksum % 10 === 0
}

export const isValidUsAccountNumber = (raw: string): boolean => {
  const candidate = raw.replace(/\s/g, '')

  return /^\d{4,17}$/.test(candidate)
}

// ─── Zod schemas per method / rail ──────────────────────────────────────────
// Every message is an i18n key; the form resolves it via `formatMessage`.

const V = {
  required: 'payout.validation.required',
  email: 'payout.validation.email',
  iban: 'payout.validation.iban',
  bic: 'payout.validation.bic',
  routing: 'payout.validation.routing',
  accountNumber: 'payout.validation.accountNumber',
  walletAddress: 'payout.validation.walletAddress',
  country: 'payout.validation.country',
  countryUs: 'payout.validation.countryUs',
  usdOnly: 'payout.validation.usdOnly',
} as const

const required = (max: number) => z.string().min(1, V.required).max(max, V.required)

const addressShape = (opts: { requireState: boolean; fixedCountry?: string }) => ({
  line: required(255),
  city: required(128),
  state: opts.requireState ? required(64) : z.string().max(64, V.required).optional(),
  post_code: required(32),
  country_code: z
    .string()
    .min(1, V.required)
    .refine((v) => isIsoCountryCode(v), V.country)
    .refine(
      (v) => opts.fixedCountry === undefined || v.trim().toUpperCase() === opts.fixedCountry,
      V.countryUs,
    ),
})

const pickAddress = (values: {
  line: string
  city: string
  state?: string
  post_code: string
  country_code: string
}): AffiliatePayoutAddressSchema => ({
  line: values.line,
  city: values.city,
  state: values.state === undefined || values.state === '' ? null : values.state,
  post_code: values.post_code,
  country_code: values.country_code.toUpperCase(),
})

const paypalSchema = z
  .object({ email: z.string().min(1, V.required).email(V.email) })
  .transform((v) => ({ email: v.email }))

const requiredEnum = <const T extends readonly [string, ...string[]]>(values: T) =>
  // zod v4 renamed the custom-error key `errorMap` -> `error`. Keeping the old
  // `errorMap` key would silently drop this message (v4 ignores the unknown key and
  // falls back to its default); the `error` callback itself accepts a string or
  // `{ message }` — a bare string is simplest.
  z.enum(values, { error: () => V.required })

const cryptoSchema = z
  .object({
    network: requiredEnum(['BTC', 'USDC (erc20)']),
    address: z
      .string()
      .min(1, V.required)
      .refine((v) => /^[A-Za-z0-9_-]{10,128}$/.test(v), V.walletAddress),
  })
  .transform((v) => ({ address: v.address, network: v.network }))

const euIbanSchema = z
  .object({
    holder: required(255),
    iban: z.string().min(1, V.required).refine(isValidIban, V.iban),
    bic: z.string().min(1, V.required).refine(isValidBic, V.bic),
    ...addressShape({ requireState: false }),
  })
  .transform(
    (v): AffiliateEuIbanRailSchema => ({
      rail: 'EU_IBAN',
      holder: v.holder,
      iban: normalizeIban(v.iban),
      bic: normalizeBic(v.bic),
      address: pickAddress(v),
    }),
  )

const usAchSchema = z
  .object({
    holder: required(255),
    routing_number: z.string().min(1, V.required).refine(isValidAbaRouting, V.routing),
    account_number: z.string().min(1, V.required).refine(isValidUsAccountNumber, V.accountNumber),
    account_type: requiredEnum(['CHECKING', 'SAVINGS']),
    receiver_type: requiredEnum(['PERSON', 'INSTITUTION']),
    ...addressShape({ requireState: true, fixedCountry: 'US' }),
  })
  .transform(
    (v): AffiliateUsAchRailSchema => ({
      rail: 'US_ACH',
      holder: v.holder,
      routing_number: v.routing_number.trim(),
      account_number: v.account_number.replace(/\s/g, ''),
      account_type: v.account_type,
      receiver_type: v.receiver_type,
      address: pickAddress(v),
    }),
  )

const intlSwiftSchema = z
  .object({
    holder: required(255),
    account_number: z.string().min(4, V.accountNumber).max(64, V.accountNumber),
    swift: z.string().min(1, V.required).refine(isValidBic, V.bic),
    bank_name: required(255),
    tax_id: z.string().max(64, V.required).optional(),
    ...addressShape({ requireState: false }),
  })
  .transform(
    (v): AffiliateIntlSwiftRailSchema => ({
      rail: 'INTL_SWIFT',
      holder: v.holder,
      account_number: v.account_number,
      swift: normalizeBic(v.swift),
      bank_name: v.bank_name,
      tax_id: v.tax_id === undefined || v.tax_id === '' ? null : v.tax_id,
      address: pickAddress(v),
    }),
  )

const RAIL_SCHEMAS: Record<
  WireRail,
  z.ZodType<AffiliatePaymentMethodRequestSchemaBankDetailsAnyOf, unknown>
> = {
  EU_IBAN: euIbanSchema,
  US_ACH: usAchSchema,
  INTL_SWIFT: intlSwiftSchema,
}

// ─── Build + validate the write body ────────────────────────────────────────

export type PayoutFieldErrors = Record<string, string>

export type BuildPayoutResult =
  | { success: true; body: AffiliatePaymentMethodRequestSchema }
  | { success: false; fieldErrors: PayoutFieldErrors }

const firstIssueByPath = (error: z.ZodError): PayoutFieldErrors => {
  const errors: PayoutFieldErrors = {}
  for (const issue of error.issues) {
    const key = issue.path[0]
    if (typeof key === 'string' && errors[key] === undefined) {
      errors[key] = issue.message
    }
  }

  return errors
}

/**
 * Validate the flat form values for `method`/`rail` and, on success, assemble
 * the discriminated `AffiliatePaymentMethodRequestSchema` write body. Values are
 * trimmed here so schemas validate the trimmed input. On failure returns a
 * per-field error map keyed by the same field names the form renders.
 */
export const buildPaymentMethodRequest = (
  method: PayoutMethod,
  rail: WireRail | null,
  rawValues: Record<string, string>,
): BuildPayoutResult => {
  const values: Record<string, string> = {}
  for (const [key, value] of Object.entries(rawValues)) {
    values[key] = value.trim()
  }

  if (method === 'PAYPAL') {
    const parsed = paypalSchema.safeParse(values)

    return parsed.success
      ? { success: true, body: { payout_method: 'PAYPAL', paypal_details: parsed.data } }
      : { success: false, fieldErrors: firstIssueByPath(parsed.error) }
  }

  if (method === 'CRYPTO') {
    const parsed = cryptoSchema.safeParse(values)

    return parsed.success
      ? { success: true, body: { payout_method: 'CRYPTO', crypto_details: parsed.data } }
      : { success: false, fieldErrors: firstIssueByPath(parsed.error) }
  }

  if (rail === null) {
    return { success: false, fieldErrors: { rail: V.required } }
  }

  const parsed = RAIL_SCHEMAS[rail].safeParse(values)

  return parsed.success
    ? { success: true, body: { payout_method: method, bank_details: parsed.data } }
    : { success: false, fieldErrors: firstIssueByPath(parsed.error) }
}

// ─── Form-level schema (react-hook-form) ─────────────────────────────────────

/**
 * The flat shape react-hook-form holds: the two selectors plus a record of the
 * rail/method-specific detail values. `method`/`rail` empty-string is the
 * "nothing picked yet" state the selects render.
 */
export type PayoutMethodFormValues = {
  method: PayoutMethod | ''
  rail: WireRail | ''
  values: Record<string, string>
  usdOnlyConfirmed: boolean
}

/**
 * Every flat detail-field name the forms register under `values.*`, gathered
 * from the same specs that drive the rendered fields. Used to map a server
 * 422's `loc`-scoped field errors back onto the right form field.
 */
export const PAYOUT_DETAIL_FIELD_NAMES: ReadonlySet<string> = new Set(
  [...PAYPAL_FIELDS, ...CRYPTO_FIELDS, ...ADDRESS_FIELDS, ...Object.values(RAIL_FIELDS).flat()].map(
    (field) => field.name,
  ),
)

/**
 * The single schema `zodResolver` consumes. It validates the whole
 * `{ method, rail, values }` shape by delegating to {@link buildPaymentMethodRequest}
 * — the same discriminated per-method/rail schemas the standalone builder uses —
 * and re-hangs each per-field error on its react-hook-form path (`method`,
 * `rail`, or `values.<field>`). It is validation-only (no transform): the write
 * body is assembled from the validated values by {@link buildPaymentMethodRequest}
 * at submit, so the form's value type stays the flat input shape.
 */
export const payoutMethodFormSchema = z
  .object({
    method: z.enum(PAYOUT_METHODS).or(z.literal('')),
    rail: z.enum(WIRE_RAILS).or(z.literal('')),
    values: z.record(z.string(), z.string()),
    usdOnlyConfirmed: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (data.method === '') {
      ctx.addIssue({ code: 'custom', message: V.required, path: ['method'] })

      return
    }
    const result = buildPaymentMethodRequest(
      data.method,
      data.rail === '' ? null : data.rail,
      data.values,
    )
    if (!result.success) {
      for (const [field, message] of Object.entries(result.fieldErrors)) {
        ctx.addIssue({
          code: 'custom',
          message,
          path: field === 'method' || field === 'rail' ? [field] : ['values', field],
        })
      }
    }
  })

/**
 * The partner's own form: payouts are always sent in USD and no payout detail
 * records the account currency, so the partner must confirm the account receives
 * USD. The admin form keeps {@link payoutMethodFormSchema} — the server only
 * enforces the confirmation on the partner endpoint, so an admin can correct data.
 */
export const partnerPayoutMethodFormSchema = payoutMethodFormSchema.superRefine((data, ctx) => {
  if (!data.usdOnlyConfirmed) {
    ctx.addIssue({ code: 'custom', message: V.usdOnly, path: ['usdOnlyConfirmed'] })
  }
})

// ─── Detail summary (CSV export + read views) ───────────────────────────────

const readStr = (details: Record<string, unknown>, key: string): string => {
  const value = details[key]

  return typeof value === 'string' ? value : ''
}

const joinParts = (parts: string[]): string => parts.filter((p) => p !== '').join(' · ')

const summarizeUnknown = (details: Record<string, unknown>): string => {
  const parts = Object.entries(details)
    .filter(([, value]) => typeof value === 'string' && value !== '')
    .map(([key, value]) => `${key}: ${String(value)}`)

  return parts.length > 0 ? parts.join(' · ') : ''
}

// Bank details may arrive in the new rail-discriminated shape OR the legacy
// free-form shapes (WISE: full_name+email, WIRE: bank_name+iban). Handle both.
const formatBankDetails = (details: Record<string, unknown>): string => {
  const rail = readStr(details, 'rail')
  if (rail === 'EU_IBAN') {
    return joinParts([readStr(details, 'holder'), readStr(details, 'iban')])
  }
  if (rail === 'US_ACH') {
    return joinParts([
      readStr(details, 'holder'),
      readStr(details, 'account_number'),
      readStr(details, 'routing_number'),
    ])
  }
  if (rail === 'INTL_SWIFT') {
    return joinParts([
      readStr(details, 'holder'),
      readStr(details, 'swift'),
      readStr(details, 'account_number'),
    ])
  }

  const legacyWise = joinParts([readStr(details, 'full_name'), readStr(details, 'email')])
  if (legacyWise !== '') {
    return legacyWise
  }
  const legacyWire = joinParts([readStr(details, 'bank_name'), readStr(details, 'iban')])
  if (legacyWire !== '') {
    return legacyWire
  }

  return summarizeUnknown(details)
}

const humanizeKey = (key: string): string =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

export type PayoutDetailRow = { label: string; value: string }

/**
 * Flatten stored payout details into label/value rows for the itemised read
 * views. Expands the nested `address` object one level and drops the `rail`
 * discriminator (shown via context). Tolerant of the legacy free-form shape —
 * any flat string entry renders as a row.
 */
export const flattenPayoutDetails = (
  details: Record<string, unknown> | null | undefined,
): PayoutDetailRow[] => {
  if (details == null) {
    return []
  }
  const rows: PayoutDetailRow[] = []
  for (const [key, value] of Object.entries(details)) {
    if (key === 'rail') {
      continue
    }
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      for (const [nestedKey, nestedValue] of Object.entries(value as Record<string, unknown>)) {
        if (typeof nestedValue === 'string' && nestedValue !== '') {
          rows.push({ label: humanizeKey(nestedKey), value: nestedValue })
        }
      }
    } else if (typeof value === 'string' && value !== '') {
      rows.push({ label: humanizeKey(key), value })
    }
  }

  return rows
}

/**
 * One-line human summary of a partner's stored payout details, for the CSV
 * export and the read views. Tolerant of both the new structured shape and the
 * legacy free-form dicts existing partners still carry.
 */
export const formatPayoutDetails = (
  method: string,
  details: Record<string, unknown> | null | undefined,
): string => {
  if (details == null) {
    return ''
  }
  switch (method) {
    case 'PAYPAL':
      return readStr(details, 'email')
    case 'CRYPTO': {
      const network = readStr(details, 'network')
      const address = readStr(details, 'address')

      return network !== '' && address !== ''
        ? `${network}: ${address}`
        : joinParts([network, address])
    }
    case 'WISE':
    case 'WIRE':
      return formatBankDetails(details)
    default:
      return summarizeUnknown(details)
  }
}
